import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  FlagRounded,
  TravelExploreRounded,
  WorkspacePremiumRounded,
  ArrowLeft,
  ArrowRight,
} from '@mui/icons-material';
import { headerGradient } from '../../theme';

interface EventoHistorico {
  imagem: string;
  ano: string;
  texto: string;
}

// Use caminhos estáveis via /public/assets
const historia: EventoHistorico[] = [
  {
    imagem: '/assets/timeline/historia-02-150x150.png',
    ano: '1946',
    texto: 'Decretou-se a fundação do Senac em 10 de Janeiro de 1946.',
  },
  {
    imagem: '/assets/timeline/historia-03-150x150.png',
    ano: '1969',
    texto:
      'Autoridades de Manaus em visita ao terreno onde se construiu a primeira sede Sesc/Senac no Amazonas.',
  },
  {
    imagem: '/assets/timeline/historia-04-150x150.png',
    ano: '1973',
    texto:
      'Em 25 de Janeiro inaugurou-se o primeiro Centro de Formação Profissional, o Pequeno Franco.',
  },
  {
    imagem: '/assets/timeline/historia-05-150x150.png',
    ano: '1980',
    texto: 'Curso de manicure em meados de 1980.',
  },
  {
    imagem: '/assets/timeline/historia-06-150x150.png',
    ano: '1995',
    texto: 'Inauguração da Sede Administrativa do Senac Amazonas.',
  },
  {
    imagem: '/assets/timeline/historia-07-150x150.png',
    ano: '2000',
    texto:
      'Para atender demanda específica, inaugurou-se a Balsa Escola, que desde então leva educação profissional a comunidades ribeirinhas do Amazonas.',
  },
  {
    imagem: '/assets/timeline/historia-08-150x150.png',
    ano: '2012',
    texto:
      'A fim de unificar e fortalecer sua imagem nacional, em 2012 o Senac lança sua nova marca.',
  },
  {
    imagem: '/assets/timeline/historia-09-150x150.png',
    ano: '2013',
    texto: 'A primeira Faculdade Senac da Região Norte é inaugurada em Manaus.',
  },
  {
    imagem: '/assets/timeline/campus-itacoatiara.jpeg',
    ano: '2019',
    texto: 'Demos um passo histórico com a criação do campus no município de Itacoatiara, Amazonas.',
  },
  {
    imagem: '/assets/timeline/campus-parintins.jpg',
    ano: '2021',
    texto: 'Celebramos uma grande conquista com a inauguração do campus no município de Parintins, Amazonas.',
  },
  {
    imagem: '/assets/timeline/campus-cei.jpg',
    ano: '2022',
    texto: 'Avançamos ainda mais na inovação com a inauguração do campus o Centro Especializado de Informática (CEI), em Manaus, Amazonas.',
  },
  {
    imagem: '/assets/timeline/campus-maues.jpg',
    ano: '2025',
    texto: 'Marcou um novo capítulo da nossa história, com a criação do campus do município de Maués, Amazonas.',
  },
];

const Mantenedora: React.FC = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- TIMELINE: carrossel horizontal fluído com snap ---
  const listRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateButtons = () => {
    const el = listRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateButtons();
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => updateButtons();
    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', updateButtons);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateButtons);
    };
  }, []);

  const handleNext = () => {
    const el = listRef.current;
    if (!el) return;
    const step = el.clientWidth * (isMobile ? 0.9 : isTablet ? 0.6 : 0.8);
    el.scrollBy({ left: step, behavior: 'smooth' });
  };
  const handlePrev = () => {
    const el = listRef.current;
    if (!el) return;
    const step = el.clientWidth * (isMobile ? 0.9 : isTablet ? 0.6 : 0.8);
    el.scrollBy({ left: -step, behavior: 'smooth' });
  };

  return (
    // FULL-WIDTH: fora de Container, com padding lateral responsivo
    <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3, md: 6 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Título com sublinhado na cor secundária (laranja) */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: { xs: 2.5, md: 3 },
          textTransform: 'uppercase',
          fontWeight: 700,
          letterSpacing: { xs: 0.5, md: 0.2 },
          position: 'relative',
          display: 'inline-block',
          pb: 1,
          textAlign: { xs: 'center', md: 'left' },
          fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: { xs: '50%', md: 0 },
            bottom: 0,
            transform: { xs: 'translateX(-50%)', md: 'none' },
            width: { xs: 56, sm: 72, md: 80 },
            height: 4,
            backgroundColor: theme.palette.secondary.main,
            borderRadius: theme.shape.borderRadius,
          },
        }}
      >
        Mantenedora
      </Typography>

      {/* Texto institucional atualizado */}
      <Typography
        paragraph
        textAlign="justify"
        sx={{
          lineHeight: { xs: 1.7, md: 1.8 },
          fontSize: { xs: '0.98rem', sm: '1.02rem', md: '1.06rem' },
          textAlign: { xs: 'justify', md: 'left' },
          textIndent: { xs: '1em', sm: '1.5em', md: 0 },
        }}
      >
        A mantenedora da Faculdade de Tecnologia Senac Amazonas é o Serviço Nacional de Aprendizagem Comercial – Senac, administrado no estado pelo Conselho Regional do Senac Amazonas (Senac AM). Essa entidade é responsável por garantir a gestão institucional, financeira e acadêmica da Faculdade, integrando-a à missão nacional do Senac de educar para o trabalho no comércio de bens, serviços e turismo, com foco em inovação, inclusão e excelência.
      </Typography>
      <Typography
        paragraph
        textAlign="justify"
        sx={{
          lineHeight: { xs: 1.7, md: 1.8 },
          fontSize: { xs: '0.98rem', sm: '1.02rem', md: '1.06rem' },
          textAlign: { xs: 'justify', md: 'left' },
          textIndent: { xs: '1em', sm: '1.5em', md: 0 },
        }}
      >
        A gestão do Senac Amazonas é conduzida por José Roberto Tadros Júnior, presidente do Conselho Regional do Senac Amazonas. A Direção Regional está sob a liderança da Profa. Silvana Maria Ferreira de Carvalho, que coordena as ações estratégicas da instituição no estado.
      </Typography>
      <Typography
        paragraph
        textAlign="justify"
        sx={{
          lineHeight: { xs: 1.7, md: 1.8 },
          fontSize: { xs: '0.98rem', sm: '1.02rem', md: '1.06rem' },
          textAlign: { xs: 'justify', md: 'left' },
          textIndent: { xs: '1em', sm: '1.5em', md: 0 },
        }}
      >
        Alinhado à sua visão de transformar vidas e fortalecer o setor em que atua, e orientado por valores como ética, transparência, diversidade, inovação, sustentabilidade e transformação social, o Senac Amazonas mantém a Faculdade de Tecnologia como uma de suas frentes estratégicas. Essa iniciativa reafirma seu compromisso com a empregabilidade, a inclusão, a interiorização e o fortalecimento do ensino superior voltado às reais demandas do mundo do trabalho.
      </Typography>
      <Typography
        paragraph
        textAlign="justify"
        sx={{
          lineHeight: { xs: 1.7, md: 1.8 },
          fontSize: { xs: '0.98rem', sm: '1.02rem', md: '1.06rem' },
          textAlign: { xs: 'justify', md: 'left' },
          textIndent: { xs: '1em', sm: '1.5em', md: 0 },
        }}
      >
        A Faculdade de Tecnologia Senac Amazonas está localizada na Rua 10 de Julho, nº 11, no Centro de Manaus, e conta com unidades vinculadas que fortalecem sua capilaridade e atuação regional: o Centro de Educação Profissional “Matheus Penna Ribeiro”, em Parintins; o Centro de Educação Profissional “Moisés Benarrós Israel”, em Itacoatiara; e o Centro Especializado de Informática “Manoel Catharino”, em Manaus, ampliando o alcance da educação profissional de qualidade no interior e na capital.
      </Typography>

      {/* Assinaturas */}
      <Typography
        variant="subtitle1"
        align="center"
        mt={{ xs: 3, md: 4 }}
        sx={{ textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '0.95rem', md: '1rem' } }}
      >
        José Roberto Tadros Júnior – Presidente
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        sx={{ textAlign: { xs: 'center', md: 'left' }, fontSize: { xs: '0.95rem', md: '1rem' } }}
      >
        Silvana Carvalho – Diretora do Senac Amazonas
      </Typography>

{/* Cards com gradiente do tema */}
<Grid container spacing={{ xs: 2.5, md: 4 }} justifyContent="center" mt={{ xs: 3, md: 4 }}>
  {[
    {
      title: 'Missão',
      text: 'Educar para o trabalho, de forma inovadora e inclusiva, em atividades do comércio de bens, serviços e turismo.',
      icon: (
        <FlagRounded
          sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: theme.palette.warning.main, textShadow: '2px 2px 6px rgba(0,0,0,0.4)' }}
        />
      ),
    },
    {
      title: 'Visão',
      text: 'Transformar vidas e fortalecer o setor de comércio de bens, serviços e turismo.',
      icon: (
        <TravelExploreRounded
          sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: theme.palette.warning.main, textShadow: '2px 2px 6px rgba(0,0,0,0.4)' }}
        />
      ),
    },
    {
      title: 'Valores',
      text: 'Ética e transparência, diversidade, inovação, sustentabilidade e transformação social.',
      icon: (
        <WorkspacePremiumRounded
          sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: theme.palette.warning.main, textShadow: '2px 2px 6px rgba(0,0,0,0.4)' }}
        />
      ),
    },
  ].map((card, i) => (
    <Grid item xs={12} sm={6} md={4} key={i}>
      <Paper
        elevation={6}
        sx={{
          p: { xs: 2.5, md: 3 },
          textAlign: 'center',
          borderRadius: { xs: 1.5, md: 0 },
          background: headerGradient,
          color: '#fff',
          minHeight: { xs: 260, md: 300 }, // altura fixa
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box mb={{ xs: 1.5, md: 2 }}>{card.icon}</Box>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          {card.title}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: { xs: '0.98rem', md: '1rem' }, lineHeight: { xs: 1.55, md: 1.6 } }}>
          {card.text}
        </Typography>
      </Paper>
    </Grid>
  ))}
</Grid>


 {/* Linha do tempo / história – carrossel horizontal fluído */}
<Box
  mt={{ xs: 4, md: 6 }}
  p={{ xs: 2, md: 3 }}
  borderRadius={{ xs: 1.5, md: 0 }}
  sx={{
    bgcolor: alpha(theme.palette.info.main, 0.12),
    boxShadow: 3,
    position: 'relative',
  }}
>
  <Typography
    variant="h5"
    align="center"
    mb={{ xs: 2, md: 3 }}
    color={theme.palette.primary.main}
    sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
  >
    Conheça nossa história
  </Typography>

  <Box
    ref={listRef}
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      gap: { xs: 2, md: 3 },
      overflowX: 'auto',
      scrollSnapType: 'x mandatory',
      scrollBehavior: 'smooth',
      px: { xs: 1, md: 2 },
      py: { xs: 1.5, md: 2 },
      '&::-webkit-scrollbar': { display: 'none' },
    }}
  >
    {historia.map((item, index) => (
      <Box
        key={index}
        sx={{
          flex: '0 0 auto',
          width: { xs: 240, sm: 280, md: 320 },
          scrollSnapAlign: 'start',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          letterSpacing={2}
          color={theme.palette.primary.main}
          sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
        >
          {item.ano}
        </Typography>
        <Box
          width={{ xs: 12, md: 15 }}
          height={{ xs: 12, md: 15 }}
          mx="auto"
          my={1}
          borderRadius="50%"
          sx={{
            bgcolor: theme.palette.secondary.main,
            border: `3px solid ${theme.palette.background.paper}`,
          }}
        />
        <Paper
  sx={{
    p: { xs: 1.5, md: 2 },
    backgroundColor: alpha(theme.palette.secondary.main, 0.22),
    border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
    borderRadius: { xs: 1.5, md: 0 },
    boxShadow: 1,
    height: { xs: 250, md: 260 }, // <-- altura fixa agora
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  }}
>
          <Box
            component="img"
            src={item.imagem}
            alt={item.ano}
            sx={{
              width: { xs: 86, sm: 96, md: 100 },
              height: { xs: 86, sm: 96, md: 100 },
              borderRadius: '50%',
              mb: 1,
              objectFit: 'cover',
              mx: 'auto',
            }}
          />
          <Typography
            variant="body2"
            color={theme.palette.primary.main}
            sx={{ fontSize: { xs: '0.92rem', md: '0.98rem' }, lineHeight: { xs: 1.45, md: 1.55 } }}
          >
            {item.texto}
          </Typography>
        </Paper>
      </Box>
    ))}
  </Box>


        {/* Botões de navegação flutuantes */}
        <IconButton
          onClick={handlePrev}
          aria-label="Anterior"
          disabled={!canLeft}
          sx={{
            position: 'absolute',
            top: '50%',
            left: { xs: 4, md: 8 },
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: 2,
            p: { xs: 0.5, md: 1 },
            '&:hover': { bgcolor: theme.palette.background.paper },
          }}
        >
          <ArrowLeft sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, md: 24 } }} />
        </IconButton>
        <IconButton
          onClick={handleNext}
          aria-label="Próximo"
          disabled={!canRight}
          sx={{
            position: 'absolute',
            top: '50%',
            right: { xs: 4, md: 8 },
            transform: 'translateY(-50%)',
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: 2,
            p: { xs: 0.5, md: 1 },
            '&:hover': { bgcolor: theme.palette.background.paper },
          }}
        >
          <ArrowRight sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, md: 24 } }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Mantenedora;
